import { useState, useRef, useEffect } from 'react';
import { useApp } from '@context/AppContext';
import { X, Bold, Italic, Check } from 'lucide-react';

const FONTS = [
  { id: 'Space Grotesk', label: 'Space Grotesk' },
  { id: 'JetBrains Mono', label: 'JetBrains Mono' },
  { id: 'Georgia', label: 'Georgia' },
  { id: 'Arial', label: 'Arial' },
  { id: 'Courier New', label: 'Courier New' },
  { id: 'Comic Sans MS', label: 'Comic Sans' },
  { id: 'Impact', label: 'Impact' },
  { id: 'Trebuchet MS', label: 'Trebuchet' },
];

const FONT_SIZES = [12, 16, 20, 24, 32, 40, 48, 64, 80, 96];

export default function TextEditPanel({ position, initialValues, onCommit, onCancel }) {
  const { state } = useApp();
  const { canvas } = state;
  const [text, setText] = useState(initialValues?.textContent || '');
  const [fontFamily, setFontFamily] = useState(initialValues?.fontFamily || 'Space Grotesk');
  const [fontSize, setFontSize] = useState(initialValues?.fontSize || 32);
  const [bold, setBold] = useState(initialValues?.fontStyle?.includes('bold') || false);
  const [italic, setItalic] = useState(initialValues?.fontStyle?.includes('italic') || false);
  const textRef = useRef(null);

  useEffect(() => {
    // Focus the textarea on mount
    setTimeout(() => textRef.current?.focus(), 100);
  }, []);

  const handleCommit = () => {
    if (!text.trim()) return;
    onCommit({
      textContent: text.trim(),
      fontFamily,
      fontSize,
      fontStyle: `${bold ? 'bold' : 'normal'} ${italic ? 'italic' : 'normal'}`,
      colour: canvas.colour,
      x: position.x,
      y: position.y,
    });
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20 backdrop-blur-sm">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-charcoal font-display">
            {initialValues ? 'Edit Text' : 'Add Text'}
          </h3>
          <button onClick={onCancel}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* Text input */}
          <textarea
            ref={textRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type your text here..."
            rows={3}
            className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-canvas text-charcoal font-display resize-none focus:outline-none focus:ring-2 focus:ring-amber/50 focus:border-amber"
            style={{
              fontFamily,
              fontSize: Math.min(fontSize, 32),
              fontWeight: bold ? 'bold' : 'normal',
              fontStyle: italic ? 'italic' : 'normal',
              color: canvas.colour,
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleCommit();
              }
            }}
          />

          {/* Font family */}
          <div>
            <span className="text-[10px] text-slate-400 font-mono uppercase block mb-1">Font</span>
            <div className="flex flex-wrap gap-1">
              {FONTS.map(({ id, label }) => (
                <button key={id} onClick={() => setFontFamily(id)}
                  style={{ fontFamily: id }}
                  className={`px-2 py-1 text-xs rounded-lg transition-colors ${
                    fontFamily === id
                      ? 'bg-amber text-white'
                      : 'bg-slate-100 text-charcoal hover:bg-slate-200'
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Font size */}
          <div>
            <span className="text-[10px] text-slate-400 font-mono uppercase block mb-1">Size</span>
            <div className="flex flex-wrap gap-1">
              {FONT_SIZES.map((s) => (
                <button key={s} onClick={() => setFontSize(s)}
                  className={`px-2 py-1 text-xs font-mono rounded-lg transition-colors ${
                    fontSize === s
                      ? 'bg-amber text-white'
                      : 'bg-slate-100 text-charcoal hover:bg-slate-200'
                  }`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Bold / Italic + colour preview */}
          <div className="flex items-center gap-2">
            <button onClick={() => setBold(!bold)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                bold ? 'bg-amber text-white' : 'bg-slate-100 text-charcoal hover:bg-slate-200'
              }`}>
              <Bold className="w-4 h-4" />
            </button>
            <button onClick={() => setItalic(!italic)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                italic ? 'bg-amber text-white' : 'bg-slate-100 text-charcoal hover:bg-slate-200'
              }`}>
              <Italic className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-[10px] text-slate-400 font-mono">Colour:</span>
              <div className="w-6 h-6 rounded-full border border-slate-200" style={{ backgroundColor: canvas.colour }} />
            </div>
          </div>

          {/* Preview */}
          {text.trim() && (
            <div className="bg-canvas rounded-xl p-3 border border-slate-100">
              <span className="text-[10px] text-slate-400 font-mono uppercase block mb-1">Preview</span>
              <p style={{
                fontFamily,
                fontSize: Math.min(fontSize, 48),
                fontWeight: bold ? 'bold' : 'normal',
                fontStyle: italic ? 'italic' : 'normal',
                color: canvas.colour,
                lineHeight: 1.2,
                wordBreak: 'break-word',
              }}>
                {text}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 px-4 pb-4">
          <button onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-display text-slate-500 hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleCommit} disabled={!text.trim()}
            className="flex-1 py-2.5 rounded-xl bg-amber text-white text-sm font-display font-medium hover:bg-amber-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
            <Check className="w-4 h-4" />
            {initialValues ? 'Update Text' : 'Place Text'}
          </button>
        </div>

        <p className="text-[10px] text-slate-400 text-center pb-3 font-display">
          Tip: Cmd/Ctrl+Enter to place quickly
        </p>
      </div>
    </div>
  );
}
